import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { IdentityField } from 'components/form/IdentityField';
import { ILabSubscriber, ILabSubscriberSupplierDisplay, IOption } from 'interfaces';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { CheckboxAdapter } from 'components/form/CheckboxAdapter';
import { DataGridPremium, GridColDef, itIT } from '@mui/x-data-grid-premium';
import { baseCell } from 'components/common/enhanced/cells/base-cell';
import { supplierService, labSubscriberService } from 'services/api';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { required } from 'helpers/validators';
import SupplierProductsDialog from './SupplierProductsDialog';

interface SupplierLite {
  id: number;
  code: string;
  company_name?: string;
}

interface LabSubscriberFormParams {
  labSubscriber: ILabSubscriber;
  onSubmit: (values: {
    company_name: string;
    endpoint: string;
    is_active: boolean;
    suppliers: number[];
  }) => void;
}

type SupplierOption = IOption & { description?: string; company_name?: string };

const normalizeUrl = (raw: string): string => {
  const s = raw.trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
};

const LabSubscriberForm = ({ labSubscriber, onSubmit }: LabSubscriberFormParams) => {
  const { t } = useTranslation();
  const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
  const [suppliers, setSuppliers] = useState<ILabSubscriberSupplierDisplay[]>(
    labSubscriber.suppliers_display ?? []
  );
  const [addSupplierDialogOpen, setAddSupplierDialogOpen] = useState(false);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [productsDialogSupplier, setProductsDialogSupplier] = useState<SupplierLite | null>(null);

  useEffect(() => {
    supplierService
      .getAllBaseInformation()
      .then((opts) => setSupplierOptions(opts ?? []))
      .catch(() => setSupplierOptions([]));
  }, []);

  const handleSubmit = (values: { company_name: string; endpoint: string; is_active: boolean }) => {
    onSubmit({
      ...values,
      endpoint: normalizeUrl(values.endpoint),
      suppliers: suppliers.map((s) => s.id)
    });
  };

  const suppliersColumns: GridColDef[] = [
    { field: 'code', headerName: t('global.code'), flex: 1, renderCell: baseCell },
    {
      field: 'company_name',
      headerName: t('entities.supplier.company_name'),
      flex: 1,
      renderCell: baseCell
    },
    {
      field: 'products',
      headerName: t('global.products'),
      sortable: false,
      filterable: false,
      width: 140,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setProductsDialogSupplier({
              id: params.row.id,
              code: params.row.code,
              company_name: params.row.company_name ?? ''
            });
            setProductsDialogOpen(true);
          }}>
          Visualizza
        </Button>
      )
    }
  ];

  const openAddSupplierDialog = useCallback(() => {
    setAddSupplierDialogOpen(true);
  }, []);

  const closeAddSupplierDialog = useCallback(() => {
    setAddSupplierDialogOpen(false);
  }, []);

  const onAddSupplier = useCallback(
    async (values: { supplier: number }) => {
      const selected = supplierOptions.find((s) => s.id === values.supplier);
      if (!selected) return;

      const nextSuppliers = suppliers.some((s) => s.id === selected.id)
        ? suppliers
        : [
            ...suppliers,
            {
              id: selected.id,
              code: selected.code,
              company_name: selected.company_name ?? ''
            }
          ];

      setSuppliers(nextSuppliers);
      setAddSupplierDialogOpen(false);

      // Persist immediately only when editing an existing LabSubscriber
      if (labSubscriber.id) {
        const res = await labSubscriberService.update(labSubscriber.id, {
          suppliers: nextSuppliers.map((s) => s.id)
        } as any);

        // If backend update failed, rollback optimistic UI
        if (!res) {
          setSuppliers(suppliers);
        }
      }
    },
    [labSubscriber.id, supplierOptions, suppliers]
  );

  return (
    labSubscriber && (
      <ReactFinalForm onSubmit={handleSubmit} edit={!!labSubscriber.id}>
        <div className="flex flex-wrap gap-4">
          <div className="w-full md:w-1/2">
            <IdentityField
              name="company_name"
              initialValue={labSubscriber.company_name}
              component={TextFieldAdapter}
              label={t('entities.lab-subscriber.company_name')}
              type="text"
              required
            />
          </div>
          <div className="w-full md:w-1/2">
            <IdentityField
              name="endpoint"
              initialValue={labSubscriber.endpoint ?? ''}
              component={TextFieldAdapter}
              label={t('entities.lab-subscriber.endpoints')}
              placeholder="https://..."
              type="text"
            />
          </div>
          <div className="w-full md:w-1/2 flex items-center">
            <IdentityField
              name="is_active"
              label={t('entities.lab-subscriber.is_active')}
              initialValue={labSubscriber.is_active ?? true}
              component={CheckboxAdapter}
              type="checkbox"
            />
          </div>

          <div className="w-full mt-4">
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <b>{t('entities.lab-subscriber.suppliers')}</b>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={openAddSupplierDialog}>
                    {t('global.add')}
                  </Button>
                </Box>
                <Dialog
                  fullWidth
                  maxWidth="sm"
                  open={addSupplierDialogOpen}
                  onClose={closeAddSupplierDialog}>
                  <DialogTitle>
                    {t('global.add')} {t('global.supplier')}
                  </DialogTitle>
                  <DialogContent>
                    <ReactFinalForm
                      onSubmit={onAddSupplier}
                      formId="addLabSubscriberSupplier"
                      hideControls>
                      <div className="w-full my-4">
                        <IdentityField
                          name="supplier"
                          component={AutocompleteAdapter}
                          options={supplierOptions}
                          label={t('global.supplier')}
                          type="text"
                          validate={required}
                          required
                        />
                      </div>
                    </ReactFinalForm>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={closeAddSupplierDialog}>{t('actions.cancel')}</Button>
                    <Button type="submit" form="addLabSubscriberSupplier">
                      {t('global.add')}
                    </Button>
                  </DialogActions>
                </Dialog>
                <DataGridPremium
                  density="compact"
                  rows={suppliers}
                  columns={suppliersColumns}
                  localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
                  sx={{ border: 'none', height: '35vh' }}
                  disableSelectionOnClick
                  pagination
                  rowsPerPageOptions={[5, 10, 20]}
                />
                <SupplierProductsDialog
                  open={productsDialogOpen}
                  suppliers={productsDialogSupplier ? [productsDialogSupplier] : []}
                  supplier={productsDialogSupplier}
                  onClose={() => {
                    setProductsDialogOpen(false);
                    setProductsDialogSupplier(null);
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </ReactFinalForm>
    )
  );
};

export default LabSubscriberForm;
